import { format } from 'date-fns'
import useVersionInfo from 'hooks/useVersionInfo'
import React from 'react'
import type { IVersionInfo } from 'utils/interfaces'
import { getFrontendVersion } from 'utils/version'

import style from './VersionInfo.module.css'

const formatBuildTime = (isoString: string | null): string | null => {
  if (!isoString) {
    return null
  }
  return format(new Date(isoString), 'dd.MM.yyyy HH:mm:ss')
}

const formatVersionParts = (info: IVersionInfo, compact: boolean): string[] => {
  if (compact) {
    return [info.version ?? 'unknown']
  }

  const parts = [info.version ?? 'unknown']

  if (info.commit) {
    parts.push(`[${info.commit}]`)
  }

  const branchPart = [info.branch ?? '', info.gitAhead && parseInt(info.gitAhead) > 0 ? `(+${info.gitAhead})` : '']
    .filter(Boolean)
    .join(' ')

  if (branchPart) {
    parts.push(branchPart)
  }

  const timePart = formatBuildTime(info.buildTime)

  if (timePart) {
    parts.push(timePart)
  }

  return parts
}

const isCompact = (branch: string | null | undefined): boolean => !branch || branch === 'main'

const getBranchColorClass = (branch: string | null | undefined): string => {
  switch (branch) {
    case null:
    case undefined:
    case 'main':
      return style.branchMain
    case 'dev':
      return style.branchDev
    default:
      return style.branchOther
  }
}

interface Props {
  direction?: 'row' | 'column'
}

const VersionInfo: React.FC<Props> = ({ direction = 'row' }) => {
  const { data: backend, isLoading } = useVersionInfo()

  if (isLoading) {
    return null
  }

  const frontend = getFrontendVersion()
  const frontendParts = formatVersionParts(frontend, isCompact(frontend.branch))
  const backendParts = formatVersionParts(backend ?? ({} as IVersionInfo), isCompact(backend?.branch))

  const entries = [
    { label: 'Frontend', parts: frontendParts, branch: frontend.branch },
    { label: 'Backend', parts: backendParts, branch: backend?.branch },
  ]

  return (
    <div className={`${style.versionInfo}`}>
      {direction === 'column'
        ? entries.map(({ label, parts, branch }) => (
            <div key={label} className={`${getBranchColorClass(branch)} ${style.container}`}>
              <div className={style.label}>
                {label}: {parts[0]}
              </div>
              {parts.slice(1).map((part, i) => (
                <span key={i} className={style.part}>
                  {part}
                </span>
              ))}
            </div>
          ))
        : entries.map(({ label, parts, branch }) => (
            <div key={label} className={`${style.line} ${getBranchColorClass(branch)}`}>
              {label}: {parts.join(' • ')}
            </div>
          ))}
    </div>
  )
}

export default VersionInfo
